import LogManager from "./logManager";
import { callRouterLambda, parseLambdaPayload } from "./routerLambdaUtils";

const SESSION_CACHE_PREFIX = "aims_available_extensions_";

/**
 * Fetch available extensions for the organizer.
 * Response is cached for the session — extensions don't change mid-meeting.
 *
 * @param {string} meetingUuid - The per-instance Zoom meeting UUID
 * @returns {Promise<Array>} Array of extension objects, or empty array on failure
 */
export const fetchAvailableExtensions = async (meetingUuid) => {
  const cacheKey = SESSION_CACHE_PREFIX + meetingUuid;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {
      sessionStorage.removeItem(cacheKey);
    }
  }

  try {
    const envelope = await callRouterLambda("/available-extensions", { meetingUuid });

    // Backend returns a structured envelope: { statusCode, extensions: [...] }
    const extensions = envelope?.extensions;
    if (!Array.isArray(extensions)) {
      throw new Error("/available-extensions payload missing extensions array");
    }

    LogManager.info(`Fetched ${extensions.length} available extensions`);
    sessionStorage.setItem(cacheKey, JSON.stringify(extensions));
    return extensions;
  } catch (error) {
    LogManager.error("Error calling /available-extensions:", error);
    return [];
  }
};

/**
 * Fetch active agents for attendees (non-organizer).
 * NOT cached — re-fetches every time the agent selector is opened since
 * organizer may have toggled agents since last open.
 *
 * Returns null (not []) on failure so callers can tell "the host turned every agent
 * off" apart from "we couldn't reach the service" — treating a failure as an empty
 * list would wipe the caller's agent list on a transient network blip.
 *
 * @param {string} meetingUuid - The per-instance Zoom meeting UUID
 * @returns {Promise<Array|null>} Array of active agent objects, or null on failure
 */
export const fetchActiveAgents = async (meetingUuid) => {
  try {
    const envelope = await callRouterLambda("/active-agents", { meetingUuid });

    // Backend returns a structured envelope: { statusCode, agents: [...] }
    const agents = envelope?.agents;
    if (!Array.isArray(agents)) {
      throw new Error("/active-agents payload missing agents array");
    }

    LogManager.info(`Fetched ${agents.length} active agents`);
    return agents;
  } catch (error) {
    LogManager.error("Error calling /active-agents:", error);
    return null;
  }
};

/**
 * Fetch the schedule-time agent selection the organizer chose on the meetings website.
 * Organizer-only: the ScheduledAgents record is keyed by (meetingId, organizerAlias), so
 * the router injects the caller's alias server-side and an attendee's alias never matches.
 *
 * Returns null (not []) on failure so callers can tell "no scheduled agents" apart from
 * "we couldn't reach the service" — the caller must not activate anything on a null.
 *
 * @param {string} meetingId - The Zoom numeric meeting ID
 * @returns {Promise<string[]|null>} extensionIds to activate, [] when none scheduled, null on failure
 */
export const fetchScheduledAgents = async (meetingId) => {
  try {
    const envelope = await callRouterLambda("/scheduled-agents", { meetingId });

    // Backend returns { statusCode, meetingId, extensionIds: [...], scheduledBy }.
    // MAGE already drops non-ACTIVE extensions and returns an empty list (never an error)
    // for no selection / host mismatch / expired row.
    const extensionIds = envelope?.extensionIds;
    if (!Array.isArray(extensionIds)) {
      throw new Error("/scheduled-agents payload missing extensionIds array");
    }

    LogManager.info(`Fetched ${extensionIds.length} scheduled agents`);
    return extensionIds;
  } catch (error) {
    LogManager.error("Error calling /scheduled-agents:", error);
    return null;
  }
};

/**
 * Enable an extension for the meeting (organizer only).
 *
 * @param {string} meetingUuid - The per-instance Zoom meeting UUID
 * @param {string} meetingId - The Zoom 11-digit meeting ID
 * @param {string} extensionId - The extension to enable
 * @param {string} aimsUuid - The RTMS stream ID (rtmsStreamId)
 * @param {boolean} [scheduled=false] - When true, the router records enabledSource as
 *   SCHEDULED_PREFERENCE (schedule-time auto-activation) instead of ORGANIZER_TOGGLE.
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const enableExtension = async (meetingUuid, meetingId, extensionId, aimsUuid, scheduled = false) => {
  try {
    const body = { meetingUuid, meetingId, extensionId, aimsUuid };
    if (scheduled) {
      body.scheduled = true;
    }

    LogManager.info("Calling /enable-extension. Request body: " + JSON.stringify(body));

    const response = await fetch("/enable-extension", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `HTTP ${response.status}`);
    }

    sessionStorage.removeItem(SESSION_CACHE_PREFIX + meetingUuid);

    try {
      const result = await parseLambdaPayload(response, "/enable-extension");
      LogManager.info("Enable extension result:", result);
    } catch (parseErr) {
      LogManager.warn("Failed to parse enable-extension response body:", parseErr);
    }
    return { success: true };
  } catch (error) {
    LogManager.error("Error calling /enable-extension:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Disable an extension for the meeting (organizer only).
 * Disable is idempotent — disabling an already-inactive extension returns 200.
 *
 * @param {string} meetingUuid - The per-instance Zoom meeting UUID
 * @param {string} extensionId - The extension to disable
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const disableExtension = async (meetingUuid, extensionId) => {
  try {
    const body = { meetingUuid, extensionId };

    LogManager.info("Calling /disable-extension. Request body: " + JSON.stringify(body));

    const response = await fetch("/disable-extension", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `HTTP ${response.status}`);
    }

    sessionStorage.removeItem(SESSION_CACHE_PREFIX + meetingUuid);

    try {
      const result = await parseLambdaPayload(response, "/disable-extension");
      LogManager.info("Disable extension result:", result);
    } catch (parseErr) {
      LogManager.warn("Failed to parse disable-extension response body:", parseErr);
    }
    return { success: true };
  } catch (error) {
    LogManager.error("Error calling /disable-extension:", error);
    return { success: false, error: error.message };
  }
};
